/**
 * Coluna central: cabeçalho, histórico e editor (H04, §11.2).
 *
 * ── A nota privada é o risco caro desta tela ──────────────────────────────
 *
 * A H05 nomeia: *"mandar para o cliente o que era nota interna é o erro mais
 * caro que essa tela permite — e acontece quando os dois modos se parecem"*.
 *
 * Por isso os dois modos são visualmente diferentes DESDE JÁ, sem esperar a
 * H05: fundo, borda e rótulo mudam, o botão muda de palavra, e a bolha interna
 * no histórico é amarela e marcada. Um modo de escrita que se distingue por um
 * checkbox discreto é o desenho que produz o acidente.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Anexo, ConversaComposta } from '@cliqcrm/contracts';
import { Carregando, Defasado, Erro, Vazio } from '@/shared/ui/estados/estados';
import {
  estadoDaJanela,
  lerCapacidades,
  type Capacidades,
  type EstadoDaJanela,
} from '../lib/capacidades';
import {
  IconeAnexo,
  IconeEnviada,
  IconeEntregue,
  IconeFalhaDeEntrega,
  IconeLida,
} from '@/shared/ui/icones-da-conversa';
import { ondaReal } from '../lib/onda-do-audio';
import { Etiquetas } from '@/entities/atendimento/etiqueta';
import { Atribuir } from '@/features/atendimento/passar-para-outra-pessoa';
import { AnuncioDeMensagem } from './anuncio';
import { EditorDaConversa } from './editor';
import { diagnosticarFalha, enviosPendentesDeExibir, type EnvioLocal } from '../lib/envio';
import { grudarNoFim } from '../lib/grudar-no-fim';
import { divisorDeDia, horaDaMensagem, iniciais } from '@/shared/lib/formato';
import { iconeDoCanal, nomeDoCanal } from '@/entities/atendimento/canal';
import { NOMES_DE_SITUACAO } from '@/entities/atendimento/conversa';
import type { Historico } from '../model/historico';
import type { Envio } from '../model/usar-envio';
import { TextoFormatado } from './texto-formatado';
import type { Encerramento } from '../model/usar-encerramento';
import { Audio, Avatar, Botao, CabLinha, Chip, Icone, Identificador, Situacao } from '@/shared/ui';

type Mensagem = ConversaComposta['mensagens']['itens'][number];

export function ColunaDaConversa({
  historico,
  envios,
  encerramento,
  aoEnviar,
  aoRepetir,
  aoDescartar,
  aoVoltar,
  paraInserir,
  aoInserir,
}: {
  historico: Historico;
  envios: readonly EnvioLocal[];
  encerramento: Encerramento;
  aoEnviar: Envio['enviar'];
  aoRepetir: (id: string) => void;
  aoDescartar: (id: string) => void;
  /** Só no celular: volta à lista de conversas. */
  aoVoltar?: () => void;
  /** O texto que o "Copiar" dos preços manda para a caixa de mensagem (#1085). */
  paraInserir?: string | null;
  aoInserir?: () => void;
}): ReactNode {
  const { conversa, erro, carregando, mensagens } = historico;

  if (carregando) {
    return (
      <section className="col col--conversa">
        <Carregando forma="conversa" linhas={5} rotulo="Carregando a conversa" />
      </section>
    );
  }

  // Só sem nada na tela: com dados carregados, uma atualização que falhou
  // (o limite de requisições, a rede) não apaga o que estava certo.
  if (erro && !conversa) {
    return (
      <section className="col col--conversa col--centrada">
        <Erro
          erro={erro}
          aoRepetir={historico.recarregar}
          titulo="Não foi possível abrir a conversa"
        />
      </section>
    );
  }

  if (!conversa) return <section className="col col--conversa" />;

  return (
    <ConversaCarregada
      conversa={conversa}
      historico={historico}
      mensagens={mensagens}
      envios={envios}
      encerramento={encerramento}
      aoEnviar={aoEnviar}
      aoRepetir={aoRepetir}
      aoDescartar={aoDescartar}
      aoVoltar={aoVoltar}
      paraInserir={paraInserir ?? null}
      aoInserir={aoInserir}
    />
  );
}

/**
 * O histórico com o editor flutuando por cima (25/10).
 *
 * A barra de escrever não tem fundo: as mensagens passam por baixo dela nos
 * vãos, e só o campo, o modo e o enviar são sólidos. Para a última mensagem
 * não ficar escondida atrás da barra, o histórico ganha embaixo a altura dela,
 * medida a cada mudança (a barra cresce com o texto, o anexo, o aviso).
 */
function Palco({ editor, children }: { editor: ReactNode; children: ReactNode }): ReactNode {
  const palco = useRef<HTMLDivElement>(null);
  const rodape = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const caixa = rodape.current;
    const raiz = palco.current;
    if (!caixa || !raiz || typeof ResizeObserver === 'undefined') return;
    const medir = () => {
      const rolagem = raiz.querySelector<HTMLElement>('.historico');
      // Quem estava no fim continua no fim depois que a barra muda de altura.
      const noFim =
        rolagem !== null && rolagem.scrollHeight - rolagem.scrollTop - rolagem.clientHeight < 8;
      raiz.style.setProperty('--altura-do-editor', `${caixa.offsetHeight}px`);
      if (rolagem && noFim) rolagem.scrollTop = rolagem.scrollHeight;
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(caixa);
    return () => observador.disconnect();
  }, []);

  return (
    <div className="conversa-palco" ref={palco}>
      {children}
      <div className="conversa-palco__rodape" ref={rodape}>
        {editor}
      </div>
    </div>
  );
}

function ConversaCarregada({
  conversa,
  historico,
  mensagens,
  envios,
  encerramento,
  aoEnviar,
  aoRepetir,
  aoDescartar,
  aoVoltar,
  paraInserir,
  aoInserir,
}: {
  conversa: ConversaComposta;
  historico: Historico;
  mensagens: readonly Mensagem[];
  envios: readonly EnvioLocal[];
  encerramento: Encerramento;
  aoEnviar: Envio['enviar'];
  aoRepetir: (id: string) => void;
  aoDescartar: (id: string) => void;
  aoVoltar?: () => void;
  paraInserir: string | null;
  aoInserir: (() => void) | undefined;
}): ReactNode {
  const capacidades = useMemo(() => lerCapacidades(conversa.capabilities), [conversa.capabilities]);

  /**
   * A janela conta da última mensagem DO CLIENTE.
   *
   * Procurada nas mensagens carregadas, e não em `ultima_mensagem_em` — aquele
   * campo é da última mensagem de qualquer direção, e usá-lo faria a janela
   * parecer renovada toda vez que o vendedor escrevesse.
   *
   * Se o cliente não escreve há mais mensagens do que cabem na página de cima,
   * não achamos o carimbo e a janela conta como nunca aberta. É o certo pelo
   * lado seguro: nesse cenário ela estaria fechada de qualquer forma.
   */
  const ultimaEntradaEm = useMemo(() => {
    for (let i = mensagens.length - 1; i >= 0; i -= 1) {
      const mensagem = mensagens[i];
      if (mensagem?.direcao === 'entrada') return mensagem.enviada_em;
    }
    return null;
  }, [mensagens]);

  const janela = useJanela(capacidades, ultimaEntradaEm);

  return (
    <section className="col col--conversa" aria-label="Conversa">
      <CabecalhoDaConversa conversa={conversa} encerramento={encerramento} aoVoltar={aoVoltar} />

      {conversa.conversa.stale && (
        <Defasado
          projetadoEm={conversa.conversa.projetado_em}
          origem="Esta conversa"
          aoAtualizar={historico.recarregar}
        />
      )}

      {/* A região viva que anuncia mensagem nova (§11.1). Fica fora do
          histórico de propósito — ver o cabeçalho de `anuncio.tsx`. */}
      <AnuncioDeMensagem mensagens={mensagens} />

      <Palco
        editor={
          <EditorDaConversa
            conversaId={conversa.conversa.id}
            aoEnviar={aoEnviar}
            canal={nomeDoCanal(conversa.conversa.canal)}
            capacidades={capacidades}
            janela={janela}
            paraInserir={paraInserir}
            aoInserir={aoInserir}
          />
        }
      >
        <Historico
          historico={historico}
          mensagens={mensagens}
          capacidades={capacidades}
          conversaId={conversa.conversa.id}
          envios={envios}
          aoRepetir={aoRepetir}
          aoDescartar={aoDescartar}
        />
      </Palco>
    </section>
  );
}

/**
 * A contagem regressiva, recalculada de minuto em minuto.
 *
 * A Técnica da task diz por que ela existe: *"saber que restam duas horas muda
 * o comportamento do vendedor — ele responde antes"*. Um número congelado na
 * abertura da tela não faria isso; quem deixa a aba aberta a manhã inteira
 * veria "restam 6 h" ao meio-dia.
 *
 * De minuto em minuto, e não de segundo em segundo: a diferença não muda
 * decisão nenhuma, e um relógio de segundos numa tela de trabalho é ruído
 * piscando no canto do olho o dia inteiro.
 */
function useJanela(capacidades: Capacidades, ultimaEntradaEm: string | null): EstadoDaJanela {
  const [agora, definirAgora] = useState(() => Date.now());

  useEffect(() => {
    if (!capacidades.janela.existe) return;
    const relogio = setInterval(() => definirAgora(Date.now()), 60_000);
    return () => clearInterval(relogio);
  }, [capacidades.janela.existe]);

  return useMemo(
    () => estadoDaJanela(capacidades, ultimaEntradaEm, agora),
    [capacidades, ultimaEntradaEm, agora],
  );
}

function CabecalhoDaConversa({
  conversa,
  encerramento,
  aoVoltar,
}: {
  conversa: ConversaComposta;
  encerramento: Encerramento;
  aoVoltar?: () => void;
}): ReactNode {
  const { situacao, prioridade, canal, responsavel, etiquetas } = conversa.conversa;
  const [transferindo, definirTransferindo] = useState(false);

  /*
   * Uma linha, e não um bloco de quatro.
   *
   * O cabeçalho é contexto, não conteúdo: cada linha dele empurra uma
   * mensagem para fora da tela — no celular, duas linhas são um quarto do que
   * sobra para a conversa. Nome, canal, quando abriu e com quem está cabem
   * numa linha de meta; "sem responsável" continua escrito, e não omitido,
   * porque uma conversa sem dono precisa parecer livre para alguém pegá-la.
   * As ações secundárias moram no menu de ⋯; a única visível é encerrar.
   */
  /*
   * A meta (canal, caixa, quando abriu, com quem está) saiu do cabeçalho
   * (v3): mora no painel do cliente, em Detalhes do atendimento. Aqui fica o
   * que identifica a pessoa de relance — o glifo do canal e o telefone —,
   * como no desenho.
   */
  const nome = conversa.cliente?.nome_exibicao ?? 'Contato sem cadastro';
  const glifoDoCanal = iconeDoCanal(canal);
  const telefone = conversa.cliente?.telefone_exibicao ?? null;

  return (
    <>
      <CabLinha
        nome={nome}
        inicio={
          <Avatar
            nome={conversa.cliente?.nome_exibicao}
            semente={conversa.cliente?.id}
            tamanho={36}
          />
        }
        canal={
          glifoDoCanal ? (
            <span className="cab-linha__glifo" data-canal={glifoDoCanal}>
              <Icone nome={glifoDoCanal} rotulo={nomeDoCanal(canal) ?? undefined} />
            </span>
          ) : undefined
        }
        /*
          A situação sai do chip da direita e vai para baixo do nome, como
          ponto e palavra (v3): "● Aberta". Verde quando está aberta — é a
          conversa viva —, cinza nos outros estados.
        */
        situacao={
          <Situacao
            texto={NOMES_DE_SITUACAO[situacao] ?? situacao}
            tom={situacao === 'aberta' ? 'ok' : situacao === 'pendente' ? 'atencao' : 'neutro'}
          />
        }
        meta={telefone ?? undefined}
        etiquetas={
          <>
            {/* `?? []` porque num deploy contínuo a tela pode ficar minutos à
                frente da API, e a conversa inteira não pode quebrar por um
                campo que ainda não existe do outro lado. */}
            <Etiquetas titulos={etiquetas ?? []} emLinha />
          </>
        }
        direita={
          <>
            {/*
              O "+ etiqueta" saiu daqui (v3): mora na linha "Tags" do painel
              do cliente, à direita, que é onde o desenho o põe. As etiquetas
              continuam à vista no slot do meio.
            */}
            {prioridade && prioridade !== 'media' && (
              <Chip tom={prioridade === 'urgente' ? 'erro' : 'neutro'}>
                {prioridade === 'urgente' ? 'Urgente' : prioridade}
              </Chip>
            )}
            {/*
              "Atribuir" à vista, ao lado de Encerrar (v3, #677). Era o único
              item do menu de ⋯ — "Passar para outra pessoa…" — e um menu de
              um item só escondia a ação mais usada do cabeçalho atrás de um
              clique. Abre o MESMO diálogo de antes.
            */}
            <Botao variante="primario" onClick={() => definirTransferindo(true)}>
              <Icone nome="passar" />
              Atribuir
            </Botao>
            <BotaoDeEncerrar situacao={situacao} encerramento={encerramento} />
          </>
        }
        aoVoltar={aoVoltar}
        rotuloVoltar="Voltar às conversas"
      />
      <Atribuir
        alvo={{ tipo: 'conversa', id: conversa.conversa.id }}
        responsavelAtual={responsavel?.referencia_externa ?? null}
        aberto={transferindo}
        aoFechar={() => definirTransferindo(false)}
      />
    </>
  );
}

/**
 * Encerrar e reabrir (D08).
 *
 * -- Os tres estados, e por que o do meio existe -------------------------
 *
 * A rota responde 202: a engine confirmou, e os efeitos (carimbo, atividade,
 * tarefa de proxima acao) chegam pelo evento de volta. Pular direto para
 * "encerrada" seria afirmar o que ainda nao aconteceu, que e o que a 14.3
 * proibe. Por isso existe o "aguardando o registro" — e por isso ele tem
 * prazo: espera eterna e a mesma mentira, mais lenta.
 *
 * -- Sem dialogo de confirmacao, e o motivo ------------------------------
 *
 * Encerrar tem volta pelo proprio botao, que vira "Reabrir" assim que a
 * projecao chega. Confirmacao para acao reversivel treina a pessoa a clicar em
 * "sim" sem ler — e ai ela nao protege a acao irreversivel de verdade, quando
 * aparecer.
 */
function BotaoDeEncerrar({
  situacao,
  encerramento,
}: {
  situacao: string;
  encerramento: Encerramento;
}): ReactNode {
  const { estado, encerrar, reabrir, dispensar } = encerramento;
  const resolvida = situacao === 'resolvida';

  if (estado.nome === 'enviando' || estado.nome === 'aguardando') {
    const verbo = estado.desejada === 'resolvida' ? 'Encerrando' : 'Reabrindo';
    const detalhe = estado.nome === 'enviando' ? 'falando com o canal' : 'aguardando o registro';

    return (
      <span className="encerramento encerramento--curso" role="status">
        {verbo}… <small>{detalhe}</small>
      </span>
    );
  }

  if (estado.nome === 'demorou') {
    return (
      <span className="encerramento encerramento--demorou" role="status">
        {/*
          Nao diz que falhou, porque nao se sabe: o canal confirmou, e o que nao
          chegou foi o registro daqui. Dizer "erro" mandaria a pessoa repetir
          uma acao que ja aconteceu do outro lado.
        */}
        O canal registrou, e o histórico daqui ainda não atualizou.{' '}
        <button type="button" onClick={dispensar}>
          Entendi
        </button>
      </span>
    );
  }

  if (estado.nome === 'falhou') {
    return (
      <span className="encerramento encerramento--falhou" role="alert">
        Não foi possível {estado.desejada === 'resolvida' ? 'encerrar' : 'reabrir'}.{' '}
        <button type="button" onClick={estado.desejada === 'resolvida' ? encerrar : reabrir}>
          Tentar de novo
        </button>
      </span>
    );
  }

  return (
    <Botao
      onClick={resolvida ? reabrir : encerrar}
      title={
        resolvida
          ? 'Reabre a conversa no canal de atendimento.'
          : 'Encerra a conversa no canal de atendimento. Dá para reabrir depois.'
      }
    >
      {resolvida ? 'Reabrir' : 'Encerrar'}
    </Botao>
  );
}

function Historico({
  historico,
  mensagens,
  capacidades,
  conversaId,
  envios,
  aoRepetir,
  aoDescartar,
}: {
  historico: Historico;
  mensagens: readonly Mensagem[];
  capacidades: Capacidades;
  conversaId: string;
  envios: readonly EnvioLocal[];
  aoRepetir: (id: string) => void;
  aoDescartar: (id: string) => void;
}): ReactNode {
  const rolagem = useRef<HTMLDivElement>(null);

  /*
   * O fim acompanha quem está no fim — ver `grudar-no-fim.ts`.
   *
   * A versão anterior rolava quando o ID da última mensagem mudava, e isso
   * falhava justamente no uso real: a bolha entra na tela antes de a foto
   * carregar, então rolávamos para uma altura que a imagem ainda ia aumentar —
   * e a mensagem que acabou de chegar ficava escondida abaixo da dobra.
   *
   * Agora o gatilho é o certo: o conteúdo ficou mais alto. Quem subiu para ler
   * o histórico continua onde estava.
   */
  /*
   * Re-arma a CADA conversa, e não uma vez só.
   *
   * O componente não desmonta ao trocar de conversa — muda o `conversaId` e
   * pronto. Com dependência vazia, a segunda conversa herdava a posição da
   * primeira: quem tinha subido para ler o histórico de uma abria a seguinte
   * no meio, sem entender por quê.
   */
  useEffect(() => {
    const elemento = rolagem.current;
    if (!elemento) return undefined;
    return grudarNoFim(elemento);
  }, [conversaId]);

  /*
   * A conversa entra na conta, e não é redundância com o hook.
   *
   * O hook já entrega só os desta conversa; aqui a regra é afirmada de novo no
   * lugar que DESENHA. Em 02/09/2026 a fila não tinha recorte nenhum e a mesma
   * mensagem apareceu em duas conversas — a barreira barata fica nos dois
   * lados.
   */
  const locais = enviosPendentesDeExibir(envios, mensagens, conversaId);

  if (mensagens.length === 0 && locais.length === 0) {
    // "Você pode começar" pode ser mentira: o WhatsApp exige que o cliente
    // escreva primeiro. Afirmar sem consultar as capabilities é o erro que a
    // §8.2 nomeia — o vendedor escreve, tenta enviar, e perde o texto.
    const naoPodeIniciar = capacidades.podeIniciar === false;

    return (
      <div className="col__rolagem col__rolagem--centrada">
        <Vazio
          titulo="Nenhuma mensagem ainda"
          porque={
            naoPodeIniciar
              ? (capacidades.motivoParaNaoIniciar ??
                'Este canal não permite iniciar a conversa — é o cliente que precisa escrever primeiro.')
              : 'Esta conversa foi aberta mas ninguém escreveu. Você pode começar.'
          }
        />
      </div>
    );
  }

  let diaAnterior = '';

  return (
    <div className="col__rolagem historico" ref={rolagem}>
      {historico.temMaisAntigas && (
        <div className="historico__acima">
          <Botao
            variante="fantasma"
            miudo
            onClick={historico.carregarMaisAntigas}
            desabilitado={historico.carregandoAntigas}
            motivo="Carregando as mensagens anteriores…"
          >
            {historico.carregandoAntigas ? 'Carregando…' : 'Carregar mensagens anteriores'}
          </Botao>
        </div>
      )}

      {historico.erroAoCarregarAntigas != null && (
        <div className="historico__acima">
          <Erro
            erro={historico.erroAoCarregarAntigas}
            aoRepetir={historico.carregarMaisAntigas}
            titulo="Não foi possível carregar o que veio antes"
          />
        </div>
      )}

      {mensagens.map((mensagem) => {
        const dia = divisorDeDia(mensagem.enviada_em);
        const novoDia = dia !== diaAnterior;
        diaAnterior = dia;

        return (
          <div key={mensagem.referencia_externa}>
            {novoDia && <div className="historico__dia">{dia}</div>}
            <Bolha mensagem={mensagem} />
          </div>
        );
      })}

      {/* Os envios locais vão DEPOIS de tudo, na ordem em que foram feitos:
          são os mais recentes por construção, e é onde o olho está. */}
      {locais.map((envio) => (
        <BolhaLocal key={envio.id} envio={envio} aoRepetir={aoRepetir} aoDescartar={aoDescartar} />
      ))}
    </div>
  );
}

/**
 * A bolha de um envio que ainda não voltou do servidor — ou que foi recusado.
 *
 * ── Pendente NÃO pode parecer enviada ────────────────────────────────────
 *
 * É a Armadilha da H06: *"a API recusa por janela do WhatsApp, o indicador já
 * está verde, e o vendedor acha que respondeu"*. Por isso o pendente é
 * esmaecido, sem carimbo de hora, e diz "enviando" — três sinais de que ainda
 * não terminou.
 *
 * ── E ACEITA não pode parecer pendente ───────────────────────────────────
 *
 * O outro lado da mesma moeda, e faltava. A bolha dizia "enviando…" para
 * qualquer estado que não fosse falha: a API confirmava, o estado virava
 * `aceita`, e o rótulo continuava o mesmo — até a projeção trocar a bolha
 * local pela do servidor, o que depende do webhook do motor chegar.
 *
 * Nesse intervalo o vendedor via a própria mensagem esmaecida dizendo que
 * estava enviando, e concluía que não tinha ido. Alguns mandavam de novo.
 *
 * Aceita é a API tendo confirmado — isso não é otimismo, é o que a §11.1
 * autoriza: *"só AGORA o otimismo é permitido"*. Ganha hora e opacidade
 * cheia, e diz "enviada".
 *
 * ── A falha guarda o texto ───────────────────────────────────────────────
 *
 * O que o vendedor escreveu continua na tela, com o motivo e o que fazer. Sem
 * isso ele reescreve de cabeça — e a segunda versão nunca é igual à primeira.
 */
function BolhaLocal({
  envio,
  aoRepetir,
  aoDescartar,
}: {
  envio: EnvioLocal;
  aoRepetir: (id: string) => void;
  aoDescartar: (id: string) => void;
}): ReactNode {
  const interna = envio.privada;
  const falhou = envio.estado === 'falhou';
  const aceita = envio.estado === 'aceita';
  const diagnostico = falhou ? diagnosticarFalha(envio.erro) : null;

  return (
    <div
      className={`bolha bolha--minha ${interna ? 'bolha--interna' : ''} ${
        falhou ? 'bolha--falhou' : aceita ? 'bolha--aceita' : 'bolha--pendente'
      }`}
      aria-live="polite"
    >
      {interna && <div className="bolha__rotulo">Nota interna — o cliente não vê</div>}
      {/* A mesma formatação da bolha registrada (#615): a mensagem não muda de
          cara quando a API confirma. As menções do envio são ids, não nomes —
          o destaque delas chega com a mensagem registrada. */}
      <div className="bolha__texto">
        <TextoFormatado texto={envio.conteudo} />
      </div>

      {diagnostico ? (
        <div className="bolha__falha">
          <strong>{interna ? 'A nota não foi salva.' : 'A mensagem não foi enviada.'}</strong>
          <span>{diagnostico.motivo}</span>
          <span className="bolha__acao">{diagnostico.acao}</span>

          {diagnostico.correlationId && <Identificador>{diagnostico.correlationId}</Identificador>}

          <div className="bolha__botoes">
            {/* Repetir só aparece onde repetir resolve. Oferecê-lo num
                FORA_DA_JANELA ensinaria a clicar em algo que nunca funciona. */}
            {diagnostico.podeRepetir && (
              <button type="button" onClick={() => aoRepetir(envio.id)}>
                Tentar de novo
              </button>
            )}
            <button type="button" onClick={() => aoDescartar(envio.id)}>
              Descartar
            </button>
          </div>
        </div>
      ) : (
        <div className="bolha__rodape">
          {aceita ? (
            <>
              {/*
                A hora é a que o SERVIDOR carimbou (`aceitoEm`), e não a do
                clique: quem manda no horário da conversa é o motor, e uma
                diferença de segundos entre esta bolha e a que a projeção
                trouxer depois faria a mensagem parecer duas.
              */}
              {envio.aceitoEm && (
                <time dateTime={envio.aceitoEm}>{horaDaMensagem(envio.aceitoEm)}</time>
              )}
              {/*
                A nota interna continua com PALAVRA, e não com traço.

                Traço é vocabulário de entrega — "chegou no aparelho do
                cliente" —, e nota interna não vai para aparelho nenhum. Um
                traço nela sugeriria envio, que é justamente a confusão que
                separa nota de mensagem, e a mais cara desta tela.
              */}
              {interna ? (
                <span className="bolha__entrega">salva</span>
              ) : (
                <span
                  className="bolha__entrega bolha__entrega--aguardando"
                  role="img"
                  aria-label="enviada"
                >
                  <IconeEnviada />
                </span>
              )}
            </>
          ) : (
            <span className="bolha__enviando">enviando…</span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * O que cada estado de entrega mostra, e o que ele DIZ.
 *
 * ── Por que traço, e não palavra ──────────────────────────────────────────
 *
 * "entregue" e "lida" ocupavam uma linha inteira de texto no rodapé de toda
 * bolha, e o rodapé aparece em cada mensagem — o ruído somado é a metade da
 * conversa. O traço diz o mesmo em 16 px, e o vendedor já lê esse vocabulário
 * dezenas de vezes por dia no telefone dele.
 *
 * ── A palavra não some: ela vai para o leitor de tela ─────────────────────
 *
 * Cada estado leva um rótulo em `aria-label`. Quem enxerga vê o traço; quem
 * usa leitor de tela ouve "entregue". Trocar texto por ícone sem isso teria
 * apagado a informação para uma parte das pessoas.
 */
const ENTREGA: Record<string, { rotulo: string; icone: () => ReactNode; titulo?: string }> = {
  aguardando: { rotulo: 'enviada', icone: IconeEnviada },
  confirmado: { rotulo: 'entregue', icone: IconeEntregue },
  lido: { rotulo: 'lida', icone: IconeLida },
  falhou: { rotulo: 'falhou', icone: IconeFalhaDeEntrega },
  /*
   * Canal que não confirma NUNCA vira ícone de falha (§8.2).
   *
   * Um traço só, cinza: é a verdade sobre o que sabemos — a mensagem saiu
   * daqui, e este canal não avisa o que aconteceu depois. O `title` explica,
   * para quem parar o mouse em cima e estranhar a ausência do segundo traço.
   */
  nao_suportado: {
    rotulo: 'enviada — este canal não confirma entrega',
    icone: IconeEnviada,
    titulo: 'Este canal não confirma entrega nem leitura',
  },
  desconhecido: {
    rotulo: 'enviada — sem confirmação deste canal',
    icone: IconeEnviada,
    titulo: 'Este canal não confirma entrega nem leitura',
  },
};

/**
 * O traço de entrega no rodapé da bolha.
 *
 * O `title` da falha ganha do genérico: quando a mensagem falhou, o que a
 * pessoa precisa é do motivo e da ação, não da explicação sobre o canal.
 */
function MarcaDeEntrega({
  entrega,
}: {
  entrega: NonNullable<Mensagem['confirmacao_entrega']>;
}): ReactNode {
  const marca = ENTREGA[entrega.estado];
  if (!marca) return <span className="bolha__entrega">{entrega.estado}</span>;

  const Icone = marca.icone;
  const titulo = entrega.falha ? `${entrega.falha.motivo} — ${entrega.falha.acao}` : marca.titulo;

  return (
    <span
      className={`bolha__entrega bolha__entrega--${entrega.estado}`}
      role="img"
      aria-label={marca.rotulo}
      title={titulo}
    >
      <Icone />
    </span>
  );
}

/**
 * Os anexos de uma mensagem, dentro da bolha.
 *
 * ── Três formas, e não uma lista de links ─────────────────────────────────
 *
 * Áudio TOCA, imagem APARECE, o resto vira link. É o que o WhatsApp faz, e a
 * razão não é estética: a pessoa que atende recebe um áudio no meio de uma
 * conversa e precisa ouvi-lo ali. Um link "audio.ogg" obrigaria a baixar,
 * trocar de janela e voltar — e no meio de um atendimento isso é o suficiente
 * para o áudio não ser ouvido.
 *
 * ── A URL vale cinco minutos ──────────────────────────────────────────────
 *
 * Ela vem assinada da API e expira. Uma conversa aberta há muito tempo tem
 * links vencidos, e recarregar é o que os renova — por isso o `onError` do
 * áudio e da imagem diz isso em vez de mostrar um ícone quebrado, que só
 * pareceria defeito nosso.
 */
function Anexos({ lista }: { lista: readonly Anexo[] }): ReactNode {
  if (lista.length === 0) return null;

  return (
    <div className="bolha__anexos">
      {lista.map((anexo) => (
        <UmAnexo key={anexo.id} anexo={anexo} />
      ))}
    </div>
  );
}

function UmAnexo({ anexo }: { anexo: Anexo }): ReactNode {
  const [falhou, definirFalhou] = useState(false);
  /*
   * A onda vem do ÁUDIO REAL, calculada no navegador no primeiro toque — e
   * não de um padrão fixo, nem antes de alguém pedir: decodificar trinta
   * áudios ao abrir a conversa é caro à toa. Até lá o componente mostra o
   * trilho, que é a resposta honesta para o que ainda não se sabe.
   */
  const [picos, definirPicos] = useState<number[] | null>(null);
  const jaAnalisou = useRef(false);
  const analisarUmaVez = (): void => {
    if (jaAnalisou.current) return;
    jaAnalisou.current = true;
    void ondaReal(anexo.url).then((medida) => {
      if (medida) definirPicos(medida);
    });
  };

  if (falhou) {
    return (
      <div className="bolha__anexo-falhou">
        Este anexo não abriu. O link vale poucos minutos — recarregue a conversa.
      </div>
    );
  }

  if (anexo.tipoArquivo === 'audio') {
    return (
      <Audio
        src={anexo.url}
        picos={picos}
        aoTocar={analisarUmaVez}
        aoFalhar={() => definirFalhou(true)}
      />
    );
  }

  if (anexo.tipoArquivo === 'image') {
    return (
      <a className="bolha__imagem" href={anexo.url} target="_blank" rel="noreferrer">
        <img src={anexo.url} alt="Imagem enviada na conversa" onError={() => definirFalhou(true)} />
      </a>
    );
  }

  /*
   * Vídeo TOCA na bolha, como o áudio e a imagem — e pela mesma razão.
   *
   * Antes ele caía no ramo de arquivo e virava "Arquivo .mp4" com um link. Quem
   * atende recebia um vídeo do cliente — a foto do para-choque batido, o barulho
   * do motor — e precisava baixar, abrir noutro programa e voltar. No meio de um
   * atendimento, isso é o suficiente para o vídeo não ser visto.
   *
   * `preload="metadata"`: baixa só o cabeçalho, o bastante para desenhar o
   * quadro e a duração. `preload="auto"` faria uma conversa com cinco vídeos
   * puxar dezenas de megabytes que ninguém pediu — e a URL assinada vale cinco
   * minutos, então metade desse tráfego já venceria antes do play.
   */
  if (anexo.tipoArquivo === 'video') {
    return (
      <video
        className="bolha__video"
        src={anexo.url}
        controls
        preload="metadata"
        playsInline
        onError={() => definirFalhou(true)}
      >
        {/* Navegador sem suporte a <video> mostra isto no lugar. */}
        <a href={anexo.url} target="_blank" rel="noreferrer">
          Abrir o vídeo
        </a>
      </video>
    );
  }

  return (
    <a className="bolha__arquivo" href={anexo.url} target="_blank" rel="noreferrer">
      <IconeAnexo />
      <span className="bolha__arquivo-nome">
        {anexo.extensao ? `Arquivo .${anexo.extensao}` : 'Arquivo'}
      </span>
      <span className="bolha__arquivo-tamanho">{emKb(anexo.bytes)}</span>
    </a>
  );
}

/** Tamanho legível. Zero vira vazio: o motor nem sempre informa, e "0 KB" mentiria. */
function emKb(bytes: number): string {
  if (bytes <= 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * O disco de quem enviou, no rodapé da bolha: a foto da pessoa, ou as iniciais.
 *
 * A foto vem da autoria gravada no envio (13/10/2026). Sem foto, ou com o link
 * vencido (ele vale cinco minutos), o disco volta às iniciais — a mesma regra
 * do `Avatar`, que não tem um tamanho tão pequeno. Guardar QUAL endereço falhou,
 * e não um booleano, deixa o link novo tentar de novo.
 *
 * O nome inteiro continua no balão (`data-quem`) e no nome acessível.
 */
function QuemEnviou({ nome, foto }: { nome: string; foto: string | null }): ReactNode {
  const [falhou, definirFalhou] = useState<string | null>(null);
  const mostraFoto = foto !== null && foto !== '' && falhou !== foto;

  return (
    <span
      className="bolha__quem"
      tabIndex={0}
      role="img"
      aria-label={`Enviado por ${nome}`}
      data-quem={`Enviado por ${nome}`}
      data-foto={mostraFoto ? 'sim' : undefined}
    >
      {mostraFoto ? (
        <img
          className="bolha__quem-foto"
          src={foto}
          alt=""
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => definirFalhou(foto)}
        />
      ) : (
        iniciais(nome)
      )}
    </span>
  );
}

function Bolha({ mensagem }: { mensagem: Mensagem }): ReactNode {
  const interna = mensagem.visibilidade === 'interna';
  const minha = mensagem.direcao === 'saida';
  const entrega = mensagem.confirmacao_entrega;

  if (mensagem.direcao === 'sistema') {
    return <div className="sistema">{mensagem.conteudo}</div>;
  }

  const temTexto = mensagem.conteudo !== null && mensagem.conteudo !== '';

  /*
   * O rodapé entra NA última linha do texto, como no WhatsApp.
   *
   * Lá a hora não ocupa uma linha própria: ela fica encaixada no canto de
   * baixo, e o texto abre espaço para ela. Numa conversa de trinta mensagens
   * curtas — "ok", "ta", "pode ser" —, uma linha só para a hora quase dobra a
   * altura de cada bolha, e a conversa inteira cabe pela metade na tela.
   *
   * Só quando HÁ texto: mensagem que é só anexo não tem linha em que encaixar,
   * e o rodapé volta a ser uma linha normal embaixo da mídia.
   */
  return (
    <div
      className={[
        'bolha',
        minha ? 'bolha--minha' : 'bolha--dele',
        interna ? 'bolha--interna' : '',
        temTexto ? 'bolha--com-texto' : '',
        mensagem.menu ? 'bolha--com-menu' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {interna && <div className="bolha__rotulo">Nota interna — o cliente não vê</div>}
      {!minha && mensagem.autor.nome && <div className="bolha__autor">{mensagem.autor.nome}</div>}

      <Anexos lista={mensagem.anexos} />

      {/* Vazio quando a mensagem é só o anexo — e é assim que o WhatsApp faz.
          Um `<div>` vazio ainda ocuparia a altura da linha, empurrando o rodapé
          para longe do arquivo. */}
      {temTexto && (
        <div className="bolha__texto">
          <TextoFormatado texto={mensagem.conteudo as string} mencoes={mensagem.mencoes} />
          {/*
            O vão que reserva o canto para a hora.

            É o truque do WhatsApp, e não há alternativa boa: um elemento
            posicionado no canto NÃO empurra texto, então sem este vão a última
            linha passaria por baixo da hora. Ele é invisível e some do leitor
            de tela — quem escuta ouve o texto e depois a hora, na ordem certa.
          */}
          <span className="bolha__vao" aria-hidden="true" />
        </div>
      )}

      {mensagem.menu && (
        /* O menu como o cliente o recebe no WhatsApp: botões ou lista (#1012). */
        <ul className="bolha__menu" aria-label="Opções do menu enviado">
          {mensagem.menu.opcoes.map((opcao) => (
            <li key={opcao.id} className="bolha__opcao">
              {opcao.titulo}
            </li>
          ))}
        </ul>
      )}

      <div className="bolha__rodape">
        {/*
          Quem enviou, do NOSSO lado — em iniciais, não em frase.

          Só na saída: na entrada o autor é o cliente, e o nome dele já aparece
          acima da bolha. Aqui a pergunta é outra — "qual colega respondeu?" —,
          e numa caixa com três vendedores ela não tinha resposta na tela.

          Era a linha "enviado por: Fulano" embaixo da bolha. Ela custava uma
          linha inteira EM CADA mensagem enviada, repetindo o mesmo nome trinta
          vezes seguidas numa conversa que um vendedor só atendeu — e, desde
          que a hora passou a ficar no canto, as duas se sobrepunham.

          As iniciais respondem de relance quando o autor MUDA, que é a única
          vez em que a pergunta existe. O nome inteiro fica a um passar de
          mouse — ou a um Tab, para quem não usa mouse.
        */}
        {minha && mensagem.autor.nome && (
          <QuemEnviou nome={mensagem.autor.nome} foto={mensagem.autor.foto_url} />
        )}
        <time dateTime={mensagem.enviada_em}>{horaDaMensagem(mensagem.enviada_em)}</time>
        {/* Nota interna não sai pelo canal, então não tem o que confirmar.
            Mostrar "sem confirmação" nela sugeriria que houve tentativa de
            envio — exatamente a confusão que separa nota de mensagem. */}
        {entrega && !interna && <MarcaDeEntrega entrega={entrega} />}
      </div>
    </div>
  );
}
